import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { isInsideTelegramWebApp } from '@/lib/telegram-env';

type ThemePreference = 'auto' | 'light' | 'dark';

function readThemePreference(): ThemePreference {
  try {
    const value = localStorage.getItem('irl_theme');
    return value === 'light' || value === 'dark' ? value : 'auto';
  } catch {
    return 'auto';
  }
}

function applyColorScheme(scheme: 'light' | 'dark'): void {
  const preference = readThemePreference();
  document.documentElement.dataset.tgTheme = preference === 'auto' ? scheme : preference;
}

interface TelegramTheme {
  bgColor: string;
  textColor: string;
  hintColor: string;
  buttonColor: string;
  buttonTextColor: string;
  secondaryBgColor: string;
}

interface TelegramContextValue {
  /** True when bootstrap can proceed (initData received, or confirmed absent). */
  isReady: boolean;
  initData: string;
  /** True after timeout: inside Telegram but initData never arrived. */
  initDataMissing: boolean;
  theme: TelegramTheme;
  themePreference: ThemePreference;
  setThemePreference: (value: ThemePreference) => void;
  expand: () => void;
  close: () => void;
  showBackButton: (onClick: () => void) => void;
  hideBackButton: () => void;
  /** Temporary back action for sheets, above the current page navigation. */
  registerBackLayer: (onBack: () => void) => () => void;
}

const TelegramContext = createContext<TelegramContextValue | null>(null);

const defaultTheme: TelegramTheme = {
  bgColor: '#ffffff',
  textColor: '#202124',
  hintColor: '#68707b',
  buttonColor: '#6841df',
  buttonTextColor: '#ffffff',
  secondaryBgColor: '#f5f5f6',
};

const SDK_POLL_MS = 50;
const SDK_WAIT_MS = 2_000;
const INIT_DATA_WAIT_MS = 8_000;
const VIEWPORT_SETTLE_MS = 280;

function getTelegramWebApp(): TelegramWebApp | undefined {
  return window.Telegram?.WebApp;
}

function px(value: number): string {
  return `${String(value)}px`;
}

function applySafeAreaInsets(tg: TelegramWebApp): void {
  const content = tg.contentSafeAreaInset;
  const device = tg.safeAreaInset;

  const top = Math.max(content?.top ?? 0, device?.top ?? 0);
  const bottom = Math.max(content?.bottom ?? 0, device?.bottom ?? 0);
  const left = Math.max(content?.left ?? 0, device?.left ?? 0);
  const right = Math.max(content?.right ?? 0, device?.right ?? 0);

  document.documentElement.style.setProperty('--tg-safe-top', px(top));
  document.documentElement.style.setProperty('--tg-safe-bottom', px(bottom));
  document.documentElement.style.setProperty('--tg-safe-left', px(left));
  document.documentElement.style.setProperty('--tg-safe-right', px(right));
}

function applyStableViewport(tg: TelegramWebApp): void {
  const stableHeight = tg.viewportStableHeight;
  const viewportHeight = tg.viewportHeight;
  const stable =
    stableHeight > 0 ? stableHeight : viewportHeight > 0 ? viewportHeight : window.innerHeight;

  document.documentElement.style.setProperty('--app-height', px(stable));
  document.documentElement.style.setProperty(
    '--tg-viewport-stable-height',
    px(stableHeight > 0 ? stableHeight : stable),
  );
  document.documentElement.style.setProperty(
    '--tg-viewport-height',
    px(viewportHeight > 0 ? viewportHeight : stable),
  );
}

function bindViewportStability(tg: TelegramWebApp): () => void {
  let settleTimer = 0;
  let lastStable = 0;

  const commitHeight = (): void => {
    const stable = tg.viewportStableHeight;
    if (stable && stable > 0) {
      lastStable = stable;
    }
    applySafeAreaInsets(tg);
    applyStableViewport(tg);
  };

  const onViewportChanged = (): void => {
    window.clearTimeout(settleTimer);
    if (lastStable > 0) {
      document.documentElement.style.setProperty('--app-height', px(lastStable));
    }
    settleTimer = window.setTimeout(() => {
      commitHeight();
    }, VIEWPORT_SETTLE_MS);
  };

  commitHeight();
  tg.onEvent('viewportChanged', onViewportChanged);

  return () => {
    window.clearTimeout(settleTimer);
    tg.offEvent('viewportChanged', onViewportChanged);
  };
}

function applyCssThemeVars(p: Record<string, string | undefined>): void {
  const secondaryBg = p.secondary_bg_color ?? defaultTheme.secondaryBgColor;
  const sectionBg = p.section_bg_color ?? p.bg_color ?? defaultTheme.bgColor;

  document.documentElement.style.setProperty(
    '--tg-theme-bg-color',
    p.bg_color ?? defaultTheme.bgColor,
  );
  document.documentElement.style.setProperty(
    '--tg-theme-text-color',
    p.text_color ?? defaultTheme.textColor,
  );
  document.documentElement.style.setProperty(
    '--tg-theme-hint-color',
    p.hint_color ?? defaultTheme.hintColor,
  );
  document.documentElement.style.setProperty(
    '--tg-theme-button-color',
    p.button_color ?? defaultTheme.buttonColor,
  );
  document.documentElement.style.setProperty(
    '--tg-theme-button-text-color',
    p.button_text_color ?? defaultTheme.buttonTextColor,
  );
  document.documentElement.style.setProperty('--tg-theme-secondary-bg-color', secondaryBg);
  document.documentElement.style.setProperty('--tg-theme-section-bg-color', sectionBg);
  if (p.link_color) {
    document.documentElement.style.setProperty('--tg-theme-link-color', p.link_color);
  }
}

function applyThemeFromTelegram(
  tg: TelegramWebApp,
  setTheme: (theme: TelegramTheme) => void,
): void {
  const p = tg.themeParams;
  const secondaryBg = p.secondary_bg_color ?? defaultTheme.secondaryBgColor;

  setTheme({
    bgColor: p.bg_color ?? defaultTheme.bgColor,
    textColor: p.text_color ?? defaultTheme.textColor,
    hintColor: p.hint_color ?? defaultTheme.hintColor,
    buttonColor: p.button_color ?? defaultTheme.buttonColor,
    buttonTextColor: p.button_text_color ?? defaultTheme.buttonTextColor,
    secondaryBgColor: secondaryBg,
  });

  applyCssThemeVars(p);
  applyColorScheme(tg.colorScheme ?? 'light');
}

export function TelegramProvider({ children }: { children: ReactNode }): React.ReactElement {
  const [isReady, setIsReady] = useState(false);
  const [initData, setInitData] = useState('');
  const [initDataMissing, setInitDataMissing] = useState(false);
  const [theme, setTheme] = useState<TelegramTheme>(defaultTheme);
  const [themePreference, updateThemePreference] = useState<ThemePreference>(readThemePreference);
  const setThemePreference = useCallback((value: ThemePreference): void => {
    try {
      localStorage.setItem('irl_theme', value);
    } catch {
      // The selected theme still works when persistent storage is unavailable.
    }
    updateThemePreference(value);
    document.documentElement.dataset.tgTheme =
      value === 'auto' ? (getTelegramWebApp()?.colorScheme ?? 'light') : value;
  }, []);
  useEffect(() => {
    if (!theme) return;
    document.documentElement.dataset.tgTheme =
      themePreference === 'auto' ? (getTelegramWebApp()?.colorScheme ?? 'light') : themePreference;
  }, [themePreference, theme]);
  const backHandlerRef = useRef<(() => void) | null>(null);
  const backLayersRef = useRef<{ onBack: () => void }[]>([]);

  useEffect(() => {
    let cancelled = false;
    let sdkBound = false;
    let applyTheme: (() => void) | undefined;
    let unbindViewport: (() => void) | undefined;
    let tgRef: TelegramWebApp | undefined;
    let sdkSeenAt: number | null = null;
    let sdkPollId = 0;
    let initDataPollId = 0;
    let sdkTimeoutId = 0;

    const finishBootstrap = (missingInitData: boolean, data: string): void => {
      if (cancelled) {
        return;
      }
      setInitData(data);
      setInitDataMissing(missingInitData);
      setIsReady(true);
    };

    const bindSdk = (): TelegramWebApp | undefined => {
      if (sdkBound) {
        return tgRef;
      }

      const tg = getTelegramWebApp();
      if (!tg) {
        return undefined;
      }

      sdkBound = true;
      tgRef = tg;
      sdkSeenAt = Date.now();
      tg.ready();
      tg.expand();
      if (tg.isVersionAtLeast?.('7.7')) tg.disableVerticalSwipes?.();
      applySafeAreaInsets(tg);

      applyTheme = (): void => {
        applyThemeFromTelegram(tg, setTheme);
      };
      applyTheme();
      tg.onEvent('themeChanged', applyTheme);
      const onSafeAreaChanged = (): void => {
        applySafeAreaInsets(tg);
      };
      tg.onEvent('safeAreaChanged', onSafeAreaChanged);
      const unbindViewportStability = bindViewportStability(tg);
      unbindViewport = () => {
        unbindViewportStability();
        tg.offEvent('safeAreaChanged', onSafeAreaChanged);
      };

      return tg;
    };

    const readInitData = (tg: TelegramWebApp): string => tg.initData;

    const tryCompleteAfterSdk = (): void => {
      const tg = bindSdk();
      if (!tg) {
        return;
      }

      const data = readInitData(tg);
      if (data.length > 0) {
        window.clearInterval(initDataPollId);
        finishBootstrap(false, data);
        return;
      }

      if (!isInsideTelegramWebApp()) {
        window.clearInterval(initDataPollId);
        applyStableViewport(tg);
        applyCssThemeVars(tg.themeParams);
        applyColorScheme(tg.colorScheme ?? 'light');
        finishBootstrap(false, '');
        return;
      }

      const elapsed = Date.now() - (sdkSeenAt ?? Date.now());
      if (elapsed >= INIT_DATA_WAIT_MS) {
        window.clearInterval(initDataPollId);
        finishBootstrap(true, '');
      }
    };

    const onSdkAvailable = (): void => {
      window.clearInterval(sdkPollId);
      window.clearTimeout(sdkTimeoutId);

      bindSdk();
      tryCompleteAfterSdk();

      if (initDataPollId) {
        return;
      }

      initDataPollId = window.setInterval(() => {
        if (cancelled) {
          return;
        }
        tryCompleteAfterSdk();
      }, SDK_POLL_MS);
    };

    if (bindSdk()) {
      onSdkAvailable();
    } else {
      document.documentElement.style.setProperty('--app-height', px(window.innerHeight));

      sdkPollId = window.setInterval(() => {
        if (bindSdk()) {
          onSdkAvailable();
        }
      }, SDK_POLL_MS);

      sdkTimeoutId = window.setTimeout(() => {
        window.clearInterval(sdkPollId);
        if (!cancelled && !sdkBound) {
          applyCssThemeVars({});
          applyColorScheme('light');
          finishBootstrap(false, '');
        }
      }, SDK_WAIT_MS);
    }

    const onWindowResize = (): void => {
      if (!tgRef) {
        document.documentElement.style.setProperty('--app-height', px(window.innerHeight));
      }
    };
    window.addEventListener('resize', onWindowResize);

    return () => {
      cancelled = true;
      window.removeEventListener('resize', onWindowResize);
      window.clearInterval(sdkPollId);
      window.clearInterval(initDataPollId);
      window.clearTimeout(sdkTimeoutId);
      if (tgRef && applyTheme) {
        tgRef.offEvent('themeChanged', applyTheme);
      }
      unbindViewport?.();
    };
  }, []);

  const dispatchBack = useCallback(() => {
    const layer = backLayersRef.current.at(-1);
    if (layer) layer.onBack();
    else backHandlerRef.current?.();
  }, []);

  const syncBackButton = useCallback(() => {
    const tg = getTelegramWebApp();
    if (!tg?.isVersionAtLeast?.('6.1')) return;
    tg.BackButton.offClick(dispatchBack);
    if (backHandlerRef.current || backLayersRef.current.length > 0) {
      tg.BackButton.show();
      tg.BackButton.onClick(dispatchBack);
    } else {
      tg.BackButton.hide();
    }
  }, [dispatchBack]);

  const showBackButton = useCallback(
    (onClick: () => void) => {
      backHandlerRef.current = onClick;
      syncBackButton();
    },
    [syncBackButton],
  );

  const hideBackButton = useCallback(() => {
    backHandlerRef.current = null;
    syncBackButton();
  }, [syncBackButton]);

  const registerBackLayer = useCallback(
    (onBack: () => void) => {
      const layer = { onBack };
      backLayersRef.current.push(layer);
      syncBackButton();
      return () => {
        backLayersRef.current = backLayersRef.current.filter((item) => item !== layer);
        syncBackButton();
      };
    },
    [syncBackButton],
  );

  useEffect(() => {
    if (!isReady) return;
    syncBackButton();
    return () => {
      getTelegramWebApp()?.BackButton.offClick(dispatchBack);
    };
  }, [isReady, dispatchBack, syncBackButton]);

  const value = useMemo<TelegramContextValue>(
    () => ({
      isReady,
      initData,
      initDataMissing,
      theme,
      themePreference,
      setThemePreference,
      expand: () => getTelegramWebApp()?.expand(),
      close: () => getTelegramWebApp()?.close(),
      showBackButton,
      hideBackButton,
      registerBackLayer,
    }),
    [
      hideBackButton,
      initData,
      initDataMissing,
      isReady,
      registerBackLayer,
      showBackButton,
      theme,
      themePreference,
      setThemePreference,
    ],
  );

  return <TelegramContext.Provider value={value}>{children}</TelegramContext.Provider>;
}

export function useTelegram(): TelegramContextValue {
  const ctx = useContext(TelegramContext);
  if (!ctx) {
    throw new Error('useTelegram must be used within TelegramProvider');
  }
  return ctx;
}

interface SafeAreaInset {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

interface TelegramWebApp {
  isVersionAtLeast?: (version: string) => boolean;
  ready: () => void;
  expand: () => void;
  close: () => void;
  initData: string;
  platform: string;
  colorScheme?: 'light' | 'dark';
  viewportHeight: number;
  viewportStableHeight: number;
  safeAreaInset?: SafeAreaInset;
  contentSafeAreaInset?: SafeAreaInset;
  themeParams: Record<string, string | undefined>;
  onEvent: (event: string, handler: () => void) => void;
  offEvent: (event: string, handler: () => void) => void;
  disableVerticalSwipes?: () => void;
  HapticFeedback?: {
    impactOccurred: (style: string) => void;
    selectionChanged: () => void;
    notificationOccurred: (type: string) => void;
  };
  BackButton: {
    show: () => void;
    hide: () => void;
    onClick: (handler: () => void) => void;
    offClick: (handler: () => void) => void;
  };
}

declare global {
  interface Window {
    Telegram?: {
      WebApp: TelegramWebApp;
    };
  }
}
